import React, {ChangeEvent, useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {ChevronDown} from '@carbon/icons-react';
import {Search} from '@mui/icons-material';
import {CodeProps} from 'react-markdown/lib/ast-to-react';
import {EmptyState} from 'components/EmptyState/EmptyState';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  topic: string;
}

interface FAQData {
  content: FAQItem[];
  lastUpdated: string;
}
type FAQProps = {
  faqData: FAQData;
};

const FAQContent = ({faqData}: FAQProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const topics = [...new Set(faqData.content.map((item) => item.topic))];
  const topicFAQs = faqData.content.filter((item) => item.topic === topics[selectedTab]);

  const theme = useTheme();
  const [query, setQuery] = useState('');

  // filter FAQs based on question match
  const regex = new RegExp(query, 'i');
  const queryFAQs = faqData.content.filter((item) => regex.test(item.question));

  // filter FAQs based on question and answers match
  // const queryFAQs = faqData.filter((item) => regex.test([item.topic, item.question, item.answer].join(' ')));
  // console.log('search results: ' + queryFAQs.length);

  const handleSearchQuery = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setSelectedTab(topics.length);
  };
  console.log(setQuery);

  const handleQuerySubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSelectedTab(topics.length);
  };

  const handleTopicChange = (_event: React.SyntheticEvent, selectedTab: number) => {
    setSelectedTab(selectedTab);
    // console.log('Selected topic: ' + topics[selectedTab]);
  };

  function ImageRenderer(props: CodeProps) {
    return <img {...props} style={{maxWidth: '100%'}} />;
  }

  console.log('Selected Tab: ' + selectedTab);
  console.log('Topics length: ' + topics.length);
  console.log('Query: ' + query);
  console.log('Query FAQs length: ' + queryFAQs.length);

  return (
    <Stack>
      <Box px={6} py={2} borderBottom={`1px solid ${theme.palette.grey[500]}`}>
        <form onSubmit={handleQuerySubmit}>
          <Stack spacing={2}>
            <TextField
              placeholder='Search FAQs'
              type='search'
              variant='outlined'
              size='small'
              sx={{
                maxWidth: '623px',
                bgcolor: 'white',
                borderRadius: '6px',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{color: 'black'}} />
                  </InputAdornment>
                ),
              }}
              value={query}
              onChange={handleSearchQuery}
            />
          </Stack>
        </form>
      </Box>
      <Paper
        square
        sx={{
          minHeight: '66.5vh',
          background: theme.palette.grey[100],
          justifyContent: 'center',
          px: 6,
          py: 2,
        }}
      >
        <Typography variant='subtitle1'>Last updated on {faqData.lastUpdated}</Typography>
        <Tabs
          value={selectedTab}
          onChange={handleTopicChange}
          TabIndicatorProps={{
            style: {display: 'none'},
          }}
        >
          {/* Create a tab for each unique topic */}
          {topics.map((topic, index) => (
            <Tab
              disableRipple
              sx={{pl: index === 0 ? 0 : 2}}
              key={index}
              label={
                <Box textAlign={'left'} pr={4} sx={{borderRight: `1px solid ${theme.palette.grey[700]}`}}>
                  <Typography
                    variant='h5'
                    color={selectedTab === index ? 'secondary' : 'grey.800'}
                    fontWeight={selectedTab === index ? '600' : '400'}
                  >
                    {topic}
                  </Typography>
                </Box>
              }
            />
          ))}
          <Tab
            disableRipple
            key={'faq-search-results'}
            label={
              <Box textAlign={'left'}>
                <Typography
                  variant='h5'
                  color={selectedTab === topics.length ? 'secondary' : 'grey.800'}
                  fontWeight={selectedTab === topics.length ? '600' : '400'}
                >
                  Search Results
                </Typography>
              </Box>
            }
          />
        </Tabs>
        {/* Display FAQs based on the selected topic */}
        {selectedTab < topics.length &&
          topicFAQs.map((item) => (
            <Accordion key={item.id}>
              <AccordionSummary
                expandIcon={<ChevronDown size={20} />}
                aria-controls={`faq-content-${item.id}`}
                id={`faq-header-${item.id}`}
              >
                <Typography variant='h3'>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ReactMarkdown
                  linkTarget='_blank'
                  components={{
                    // Use the custom ImageRenderer to allow control over image max width
                    img: ImageRenderer,
                  }}
                >
                  {item.answer}
                </ReactMarkdown>
              </AccordionDetails>
            </Accordion>
          ))}
        {selectedTab == topics.length &&
          query &&
          queryFAQs.map((item) => (
            <Accordion key={item.id}>
              <AccordionSummary
                expandIcon={<ChevronDown />}
                aria-controls={`faq-search-${item.id}`}
                id={`faq-search-header-${item.id}`}
              >
                <Typography variant='h3'>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ReactMarkdown>{item.answer}</ReactMarkdown>
              </AccordionDetails>
            </Accordion>
          ))}
        {((selectedTab == topics.length && !query) || (query && queryFAQs.length === 0)) && (
          <Box width='100%' minHeight='50vh' display='flex' py={12} justifyContent='center'>
            <EmptyState title='No matching FAQs' subtitle='Please try again with different keywords' />
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default FAQContent;
